import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, X, MailPlus } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700", used: "bg-emerald-50 text-emerald-700",
  expired: "bg-slate-100 text-slate-500", revoked: "bg-rose-50 text-rose-600",
};

export default function InvitePanel({ showInviter = false }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState([]);

  const load = useCallback(() => api.get("/invites").then(({ data }) => setInvites(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/invites", { email, origin_url: window.location.origin });
      try { await navigator.clipboard.writeText(data.link); } catch { /* clipboard optional */ }
      toast.success(`Invite emailed to ${data.email} — link copied too`);
      setEmail(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send invite"); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/invites/${id}`); toast.success("Invite cancelled"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="invite-panel">
      <form onSubmit={send} className="flex flex-col sm:flex-row gap-2">
        <Input type="email" required data-testid="invite-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="friend@email.com" className="rounded-xl h-11 flex-1" />
        <Button type="submit" disabled={busy} data-testid="invite-send-btn" className="rounded-xl h-11 font-bold bg-[#FF5A79] hover:bg-[#e04865]">
          <Send className="h-4 w-4 mr-1.5" /> {busy ? "Sending…" : "Send invite"}
        </Button>
      </form>
      <p className="text-xs text-slate-400 mt-2">Each link works once, only for that email, and expires in 7 days.</p>
      <div className="mt-4 space-y-2" data-testid="invite-list">
        {invites.length === 0 && <p className="text-sm text-slate-400 flex items-center gap-2"><MailPlus className="h-4 w-4" /> No invites sent yet.</p>}
        {invites.map((i) => (
          <div key={i.invite_id} className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-2.5" data-testid={`invite-${i.invite_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-slate-800 truncate">{i.email}</div>
              <div className="text-xs text-slate-400">
                {showInviter && `By ${i.invited_by_name} · `}Sent {new Date(i.created_at).toLocaleDateString()}
                {i.status === "pending" && ` · expires ${new Date(i.expires_at).toLocaleDateString()}`}
              </div>
            </div>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize ${STATUS_STYLE[i.status]}`} data-testid={`invite-status-${i.invite_id}`}>{i.status}</span>
            {i.status === "pending" && (
              <button onClick={() => revoke(i.invite_id)} title="Cancel invite" data-testid={`invite-revoke-${i.invite_id}`}
                className="h-8 w-8 rounded-full hover:bg-rose-50 text-rose-400 hover:text-rose-600 flex items-center justify-center"><X className="h-4 w-4" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
